import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Icon } from '../components/Icon';
import { PageHero } from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { CONTACT, CONTACT_PAGE, buildWhatsAppUrl } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

export function ContactPage() {
  useDocumentMeta({
    title: 'Contacto | Corporación Indemniza',
    description: 'Cuéntanos tu caso y conversa con el equipo de Corporación Indemniza.',
    canonicalPath: '/contacto',
  });

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [service, setService] = useState('');
  const [message, setMessage] = useState('');
  const [attempted, setAttempted] = useState(false);
  const valid = name.trim().length > 1 && message.trim().length > 4;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAttempted(true);
    if (!valid) return;
    const lines = [
      `Hola, mi nombre es ${name.trim()}.`,
      service ? `Área de interés: ${service}.` : '',
      phone.trim() ? `Mi teléfono: ${phone.trim()}.` : '',
      `Mensaje: ${message.trim()}`,
    ].filter(Boolean);
    window.open(buildWhatsAppUrl(lines.join('\n')), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="contact-page">
      <PageHero
        image={CONTACT_PAGE.heroImage}
        position={CONTACT_PAGE.heroPosition}
        mobilePosition={CONTACT_PAGE.heroMobilePosition}
        eyebrow="Estamos aquí para ayudarte"
        title="Conversemos sobre tu caso"
        subtitle="Cuéntanos qué pasó y te contactamos a la brevedad para conocer inicialmente tu caso."
      />

      <section className="contact-main" aria-labelledby="contact-form-title">
        <div className="site-container contact-main__grid">
          <Reveal direction="left">
            <form className="contact-form" onSubmit={submit} noValidate>
              <p className="eyebrow">Escríbenos</p>
              <h2 id="contact-form-title">Cuéntanos tu caso</h2>
              <p className="contact-form__intro">Completa el formulario y te escribimos directo por WhatsApp.</p>

              <label className="field">
                <span>Tu nombre *</span>
                <input
                  name="name"
                  value={name}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
                  autoComplete="name"
                  required
                  minLength={2}
                  aria-invalid={attempted && name.trim().length <= 1}
                  aria-describedby={attempted && name.trim().length <= 1 ? 'contact-form-error' : undefined}
                />
              </label>
              <label className="field">
                <span>Tu teléfono (opcional)</span>
                <input
                  name="phone"
                  value={phone}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setPhone(event.target.value)}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                />
              </label>

              <fieldset className="case-types">
                <legend>¿Sobre qué necesitas ayuda?</legend>
                <div>
                  {CONTACT_PAGE.caseTypes.map((type) => (
                    <button type="button" className={service === type ? 'is-selected' : ''} onClick={() => setService(service === type ? '' : type)} key={type}>
                      {type}
                    </button>
                  ))}
                </div>
              </fieldset>

              <label className="field">
                <span>Cuéntanos qué pasó *</span>
                <textarea
                  name="message"
                  value={message}
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setMessage(event.target.value)}
                  rows={6}
                  required
                  minLength={5}
                  aria-invalid={attempted && message.trim().length <= 4}
                  aria-describedby={attempted && message.trim().length <= 4 ? 'contact-form-error' : undefined}
                />
              </label>
              {attempted && !valid ? <p className="form-error" id="contact-form-error" role="alert">Completa tu nombre y cuéntanos brevemente qué ocurrió.</p> : null}

              <button className="contact-submit" type="submit"><Icon name="whatsapp" size={19} /> Enviar por WhatsApp</button>
              <p className="contact-form__note">Al enviar, se abrirá WhatsApp con tu mensaje listo para enviar.</p>
            </form>
          </Reveal>

          <Reveal className="contact-info-column" direction="right" delay={80}>
            <section className="contact-info-card" aria-labelledby="contact-info-title">
              <h2 id="contact-info-title">Datos de contacto</h2>
              <address>
                <p><span><Icon name="mapPin" size={18} /></span>{CONTACT.address}</p>
                <a href={`tel:+${CONTACT.phoneInternational}`}><span><Icon name="phone" size={18} /></span>{CONTACT.phoneDisplay}</a>
                <a href={`mailto:${CONTACT.email}`}><span><Icon name="mail" size={18} /></span>{CONTACT.email}</a>
                <p><span><Icon name="clock" size={18} /></span>{CONTACT.hours}</p>
              </address>
            </section>
            <div className="contact-map"><iframe title="Ubicación de Corporación Indemniza" src={CONTACT.mapEmbedUrl} loading="lazy" referrerPolicy="no-referrer-when-downgrade" /></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
